import { useCallback, useEffect } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import { useFragment } from '__generated__'
import { FRAGMENT_ME } from 'api/auth/fragment-me'
import { GET_ME } from 'api/auth/me'
import { GET_DEMO_COIN_BALANCE } from 'api/demo-coins/get-demo-coin-balance'
import { featureFlags } from 'app/app-feature-flags'
import { DEMO_COIN_BALANCE_QUERY } from '../constants'

/** The balance is known and below `stake`; never for an unknown balance or stake. */
export const isShortOfDemoCoins = (
  balance: number | null,
  stake: number | null | undefined
) => balance !== null && typeof stake === 'number' && balance < stake

export interface DemoCoinBalance {
  /** A signed-in player on the off-chain stand: the balance applies. */
  isEnabled: boolean
  /**
   * The player's demo coins; `null` for a guest, off the off-chain stand and
   * before the first answer.
   */
  balance: number | null
  loading: boolean
  /** Reads the balance again for every reader on the page (the header too); nothing for a guest. */
  refetch: () => Promise<void>
  /** The balance is known and does not cover `stake` (`isShortOfDemoCoins`). */
  isShortOf: (stake: number | null | undefined) => boolean
}

interface Options {
  /**
   * A demo screen: as it opens (and on a sign-in) every reader on the page
   * reads the balance again, the header's too, which may still hold the 0
   * from right after a sign-up or a balance from before a top-up. The
   * screen's own first request is in flight then, so no request is added.
   */
  refetchAllOnOpen?: boolean
}

/**
 * The signed-in player's demo coin balance on the off-chain stand
 * (`featureFlags.offchainOnly`): a demo stake is debited from it and a payout
 * credited back. A guest plays the free rounds without coins and is never
 * asked for it (the query needs a user session). There is no subscription:
 * a screen calls `refetch` after a bet is accepted and once a round settles;
 * the header chip reads it after the daily top-up and when the tab is back
 * (`useDemoCoinBalanceRefresh`).
 */
export const useDemoCoinBalance = ({
  refetchAllOnOpen = false
}: Options = {}): DemoCoinBalance => {
  const client = useApolloClient()
  const { data: meData } = useQuery(GET_ME)
  const userId = useFragment(FRAGMENT_ME, meData?.me)?.id ?? null
  const isEnabled = featureFlags.offchainOnly && Boolean(userId)

  const { data, previousData, loading } = useQuery(GET_DEMO_COIN_BALANCE, {
    fetchPolicy: 'no-cache',
    skip: !isEnabled
  })

  // NOTE: no-cache readers share nothing, so every one of them is read again by name
  const refetch = useCallback(async () => {
    if (!isEnabled) return

    await client
      .refetchQueries({ include: [DEMO_COIN_BALANCE_QUERY] })
      .catch(() => undefined)
  }, [client, isEnabled])

  useEffect(() => {
    if (refetchAllOnOpen) void refetch()
  }, [refetch, refetchAllOnOpen])

  // NOTE: a failed refetch keeps the last answer on screen
  const balance =
    isEnabled ?
      ((data ?? previousData)?.getDemoCoinBalance.balance ?? null)
    : null

  return {
    isEnabled,
    balance,
    loading: isEnabled && loading,
    refetch,
    isShortOf: stake => isShortOfDemoCoins(balance, stake)
  }
}
